import { useEffect, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Loader2 } from "lucide-react";

// Point pdf.js to CDN worker for the exact version bundled by react-pdf
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

export default function PdfViewer({ file, className }) {
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [scale, setScale] = useState(1.15);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    setPage(1);
  }, [file]);

  return (
    <div className={`flex flex-col h-full ${className || ""}`} data-testid="pdf-viewer">
      {/* Toolbar */}
      <div className="border-b border-border bg-white px-4 py-2 flex items-center justify-between gap-3 shrink-0">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            data-testid="pdf-prev-page"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm font-mono-crm min-w-[70px] text-center">
            {page} / {numPages || "…"}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(numPages, p + 1))}
            disabled={page >= numPages}
            data-testid="pdf-next-page"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setScale((s) => Math.max(0.5, s - 0.15))}>
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="text-xs font-mono-crm min-w-[42px] text-center">
            {Math.round(scale * 100)}%
          </span>
          <Button variant="outline" size="sm" onClick={() => setScale((s) => Math.min(2.5, s + 0.15))}>
            <ZoomIn className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Document */}
      <div
        className="flex-1 overflow-auto bg-slate-200 py-6"
        ref={(el) => {
          if (el && !containerWidth) setContainerWidth(el.clientWidth);
        }}
      >
        <div className="flex justify-center">
          <Document
            file={file}
            onLoadSuccess={({ numPages }) => setNumPages(numPages)}
            loading={
              <div className="flex flex-col items-center justify-center gap-3 py-24 text-muted-foreground">
                <Loader2 className="w-6 h-6 animate-spin" />
                <p className="text-sm">Se încarcă documentul...</p>
              </div>
            }
            error={
              <div className="py-24 text-center text-red-600">
                <p className="font-medium">Nu s-a putut încărca PDF-ul.</p>
                <p className="text-sm text-muted-foreground mt-1">Încearcă să-l descarci sau să-l deschizi în tab nou.</p>
              </div>
            }
            className="shadow-lg"
          >
            <Page
              pageNumber={page}
              scale={scale}
              renderTextLayer
              renderAnnotationLayer={false}
              className="bg-white"
            />
          </Document>
        </div>
      </div>
    </div>
  );
}
